import React, { memo, useCallback, useMemo, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Advice, Icon } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';
import { EntityModalActions } from 'actions';
import AnimatedHeight from 'components/AnimatedHeight';
import { ChevronUp } from 'components/SvgIcons';
import {
    COMPANIES,
    OPPORTUNITIES,
    CONTACTS,
    ACTIVITIES,
    TASKS,
    AGENDA,
    SALESORDERS,
} from 'constants/Entities';
import { getActiveCrudName } from 'utils/crud';
import { getLiteral } from 'utils/getLiteral';
import { getError } from 'utils/Errors';
import { getFieldWorkFlow } from 'utils/fm';
import { getSmartFieldProps, getValuesFromList, getCrudFieldId } from 'utils/fm/fields';
import { logEvent } from 'utils/tracking';
import MultipleFuzzySingle from 'components/Fields/MultipleFuzzySingle';
import MultipleFuzzySingleV2 from 'components/Fields/MultipleFuzzySingleV2';
import GeoLocationField from 'components/Fields/GeoLocationField';
import DateTimeGroup from 'components/Fields/DateTimeGroup';
import DateTimeGroupV2 from 'components/Fields/DateTimeGroupV2';
import colors from 'constants/colors';
import Context from 'managers/Context';
import getRenderCustomField from './utils/getRenderCustomField';
import getClearDependencies from './utils/getClearDependencies';
import getWillBeCleared from './utils/getWillBeCleared';
import isTouched from './utils/isTouched';
import CrudField from './CrudField';
import MultipleCrudField from './MultipleCrudField';
import MultipleCrudFieldV2 from './MultipleCrudFieldV2';
import WarningChangeModal from './WarningChangeModal';
import WarningDependencyModal from './WarningDependencyModal';

const LIST_WITH_CREATE_ACTION = ['empresas', 'expedientes', 'contactos'];

const LIST_TO_ENTITY = { empresas: COMPANIES, expedientes: OPPORTUNITIES, contactos: CONTACTS };

const mapStateToProps = (state) => {
    const activeCrud = getActiveCrudName(state);
    const entityCrud = activeCrud ? state.entityCrud?.[activeCrud] : {};
    return {
        isModal: entityCrud?.isModal ?? false,
        activeCrudName: activeCrud,
        crudPermissions: state.config?.permission?.crud_permission ?? null,
        schema: entityCrud?.schema ?? [],
    };
};

const mapDispatchToProps = (dispatch) => {
    return { modalInit: bindActionCreators(EntityModalActions, dispatch).init };
};

const EntityCrudTab = memo(
    ({
        activeCrudName,
        changeField,
        clearField,
        configMap,
        crudPermissions,
        data,
        duplicates,
        dynamicHiddenMap,
        entity,
        errors,
        firstErrorField,
        firstFieldToFocus,
        idUserType,
        innerRef,
        isBulkAction,
        isModal,
        isNewEntity,
        modalInit,
        originalData,
        postComponentHeader,
        tab,
        userInviteFieldActions,
        version,
        workFlow,
        schema,
        environmentField,
        indexTab,
    }) => {
        const openWarningModalRef = useRef();
        const [collapsed, setCollapsed] = useState(false);
        const [dependencyModalContent, setDependencyModalContent] = useState(null);

        // Helper function to recursively process fields and their nested inputs
        const processFieldForCache = useCallback(
            (field, cache) => {
                if (field.smartField) {
                    // In bulk actions, we don't have data to calculate smartfield overrides
                    // but we need to mark that this field has smartfield definitions
                    if (isBulkAction) {
                        // Mark field as having active smartfield configurations that can't be evaluated
                        cache.set(field.id, { hasSmartField: true });
                    } else {
                        const overrides = getSmartFieldProps({
                            configMap,
                            data,
                            field,
                            isNewEntity,
                        });
                        cache.set(field.id, overrides);
                    }
                }

                // Recursively process nested inputs for multipleField types
                if (field.inputs && Array.isArray(field.inputs)) {
                    field.inputs.forEach((inputField) => {
                        processFieldForCache(inputField, cache);
                    });
                }
            },
            [isBulkAction, configMap, data, isNewEntity],
        );

        // Global cache for smart field overrides to avoid duplicate calls
        const smartFieldOverridesCache = useMemo(() => {
            const cache = new Map();
            const allFields = tab?.fields || [];

            allFields.forEach((field) => {
                processFieldForCache(field, cache);
            });

            return cache;
        }, [tab?.fields, processFieldForCache]);

        const onRefModal = useCallback(({ open }) => {
            openWarningModalRef.current = open;
        }, []);

        const innerChangeField = useCallback(
            (field) => {
                return (...props) => {
                    const warningChangeModal = field.inputAttrs?.warningChangeModal;
                    if (warningChangeModal && warningChangeModal?.canShow(data)) {
                        openWarningModalRef.current({
                            warningText: field.inputAttrs?.warningChangeModal?.warningText,
                            warningTitle: field.inputAttrs?.warningChangeModal?.warningTitle,
                            onConfirm: () => {
                                changeField(field.id)(...props);
                            },
                        });
                    } else {
                        changeField(field.id)(...props);
                    }
                };
            },
            [changeField, data],
        );

        const innerClearField = useCallback(
            (field) => {
                return (clear) => {
                    if (clear) {
                        const dependantFields = getClearDependencies({ field, schema });
                        if (Object.keys(dependantFields).length > 0) {
                            setDependencyModalContent({
                                parentField: field.label,
                                // We only need the array of labels for the modal
                                dependantFields: Object.values(dependantFields),
                            });
                            clear = dependantFields;
                        } else {
                            clear = field.label;
                        }
                    }
                    clearField(field)(clear);
                };
            },
            [clearField, schema],
        );

        const getFieldsActions = useCallback(
            ({ list, fieldId, type, parentFieldForContext, parentFieldForContext2 }) => {
                if (!list) return [];
                if (!isModal && list && LIST_WITH_CREATE_ACTION.includes(list)) {
                    const entityFromList = LIST_TO_ENTITY[list] || null;

                    if (!crudPermissions?.[entityFromList?.permission]?.create) {
                        return [];
                    }

                    // For those fields that have parentField, we need to be able to pick their
                    // values in order to populate their equivalent field in the context modal crud.
                    // For this reason we need to do the matching between the field of the initial crud,
                    // and the field of the context modal crud.To do it we use the parentFieldForContext
                    // and parentFieldForContext2 (to populate a second field).

                    // This object is made to allow do the matching between two equivalent fields
                    // of 2 different cruds.
                    // The companyTranslation object structure works in the following way:
                    // structureIs = {
                    //     [initialCrud]: {
                    //         [contextModalCrud]: {
                    //             [idOfParentFieldInInitialCrud]: fieldIdInContextModalCrud
                    //         }
                    //     }
                    // }

                    const companyTranslation = {
                        [AGENDA.entity]: {
                            [CONTACTS.entity]: { [parentFieldForContext]: 'IdCompany' },
                            [OPPORTUNITIES.entity]: { [parentFieldForContext]: 'idEmpresa1' },
                        },
                        [TASKS.entity]: {
                            [CONTACTS.entity]: { [parentFieldForContext]: 'IdCompany' },
                            [OPPORTUNITIES.entity]: { [parentFieldForContext]: 'idEmpresa1' },
                        },
                        [OPPORTUNITIES.entity]: {
                            [CONTACTS.entity]: { [parentFieldForContext]: 'IdCompany' },
                        },
                        [ACTIVITIES.entity]: {
                            [OPPORTUNITIES.entity]: {
                                [parentFieldForContext]: 'idEmpresa1',
                                [parentFieldForContext2]: 'idContact',
                            },
                            [CONTACTS.entity]: { [parentFieldForContext]: 'IdCompany' },
                        },
                        [SALESORDERS.entity]: {
                            [OPPORTUNITIES.entity]: {
                                [parentFieldForContext]: 'idEmpresa1',
                                [parentFieldForContext2]: 'idContact',
                            },
                            [CONTACTS.entity]: { [parentFieldForContext]: 'IdCompany' },
                        },
                    };

                    let prefilledData = {};

                    if (
                        parentFieldForContext &&
                        data?.[parentFieldForContext] &&
                        entity &&
                        entityFromList
                    ) {
                        const fieldName =
                            companyTranslation?.[entity.entity]?.[entityFromList.entity]?.[
                                parentFieldForContext
                            ] || '';
                        if (fieldName) prefilledData[fieldName] = data?.[parentFieldForContext];
                    }

                    if (
                        parentFieldForContext2 &&
                        data?.[parentFieldForContext2] &&
                        entity &&
                        entityFromList
                    ) {
                        const fieldName =
                            companyTranslation?.[entity.entity]?.[entityFromList.entity]?.[
                                parentFieldForContext2
                            ] || '';
                        if (fieldName) prefilledData[fieldName] = data?.[parentFieldForContext2];
                    }

                    // Prefill environment when creating related entity in context
                    if (environmentField?.value && entityFromList) {
                        let envFieldKey = null;
                        switch (entityFromList) {
                            case COMPANIES:
                                envFieldKey = 'branchId';
                                break;
                            case CONTACTS:
                                envFieldKey = 'idSucursal';
                                break;
                            case OPPORTUNITIES:
                                envFieldKey = 'environment';
                                break;
                            default:
                                envFieldKey = null;
                        }

                        if (envFieldKey) {
                            const envFieldId = getCrudFieldId(entityFromList, envFieldKey);
                            if (envFieldId) prefilledData[envFieldId] = environmentField.value;
                        }
                    }

                    const parentCrudInfo = {
                        id: activeCrudName,
                        fieldToChange: fieldId,
                        list,
                        type,
                    };

                    let defaultActionProps = { icon: 'addPlus' };

                    if (version === 2) {
                        defaultActionProps.iconType = 'plus';
                        delete defaultActionProps.icon;
                    }

                    switch (entityFromList) {
                        case COMPANIES:
                            return [
                                {
                                    ...defaultActionProps,
                                    label: getLiteral('page_title_accounts_create'),
                                    onClick: () => {
                                        logEvent({
                                            event: COMPANIES.trueName,
                                            submodule: 'crud',
                                            functionality: 'buttonCreateInContext',
                                        });
                                        modalInit({
                                            entity: COMPANIES,
                                            data: prefilledData,
                                            labels: {
                                                title: getLiteral('page_title_accounts_create'),
                                                success: getLiteral(
                                                    'succes_entitycreatedsuccessfully',
                                                ),
                                                error: getLiteral('label_failed_create'),
                                                middleButtonText:
                                                    getLiteral('action_save_and_detail'),
                                            },
                                            showDelete: false,
                                            parentCrudInfo,
                                        });
                                    },
                                },
                            ];
                        case CONTACTS:
                            return [
                                {
                                    ...defaultActionProps,
                                    label: getLiteral('page_title_contacts_create'),
                                    onClick: () => {
                                        logEvent({
                                            event: CONTACTS.trueName,
                                            submodule: 'crud',
                                            functionality: 'buttonCreateInContext',
                                        });
                                        modalInit({
                                            entity: CONTACTS,
                                            data: prefilledData,
                                            labels: {
                                                title: getLiteral('page_title_contacts_create'),
                                                success: getLiteral(
                                                    'succes_entitycreatedsuccessfully',
                                                ),
                                                error: getLiteral('label_failed_create'),
                                                middleButtonText:
                                                    getLiteral('action_save_and_detail'),
                                            },
                                            showDelete: false,
                                            parentCrudInfo,
                                        });
                                    },
                                },
                            ];
                        case OPPORTUNITIES:
                            return [
                                {
                                    ...defaultActionProps,
                                    label: getLiteral('page_title_opportunities_create'),
                                    onClick: () => {
                                        logEvent({
                                            event: OPPORTUNITIES.trueName,
                                            submodule: 'crud',
                                            functionality: 'buttonCreateInContext',
                                        });
                                        modalInit({
                                            entity: OPPORTUNITIES,
                                            data: prefilledData,
                                            labels: {
                                                title: getLiteral(
                                                    'page_title_opportunities_create',
                                                ),
                                                success: getLiteral(
                                                    'succes_entitycreatedsuccessfully',
                                                ),
                                                error: getLiteral('label_failed_create'),
                                                middleButtonText:
                                                    getLiteral('action_save_and_detail'),
                                            },
                                            showDelete: false,
                                            parentCrudInfo,
                                        });
                                    },
                                },
                            ];
                    }
                }

                return [];
            },
            [
                isModal,
                modalInit,
                activeCrudName,
                data,
                entity,
                crudPermissions,
                version,
                environmentField,
            ],
        );

        const renderMultipleFields = useCallback(
            (field, parentValue) => {
                let newFirstErrorField = firstErrorField;
                if (newFirstErrorField) newFirstErrorField = newFirstErrorField.toLowerCase();
                let fieldToRender = null;
                let parentField = null;

                //Sometimes backend expects a special parentField
                if (field.inputAttrs) {
                    parentField =
                        field.inputAttrs.parentFieldForBackend || field.inputAttrs.parentField;
                }

                let menuProps = field.hasInviteUsers
                    ? { actions: userInviteFieldActions }
                    : undefined;

                switch (field.type) {
                    case 'multipleFuzzySearchSingle':
                        const multipleFuzzySearchSingleProps = {
                            fieldId: field.id,
                            label: field.label,
                            inputs: field.inputs,
                            labelMode: field.labelMode || 'horizontal',
                            data,
                            onChange: changeField,
                            getError: getError,
                            errors,
                            firstErrorField: newFirstErrorField,
                            isBulkAction,
                            mandatory: field.mandatory,
                            hidden: field.hidden,
                            readOnly: field.readOnly,
                            ...field.inputAttrs,
                            parentField,
                            parentValue: parentValue ? parentValue.value || parentValue.Id : null,
                            menuProps,
                            fakeError: field.fakeError,
                            getFieldsActions,
                            onClear: innerClearField,
                            environmentField:
                                field.id !== environmentField?.id ? environmentField : undefined,
                        };

                        if (version === 2) {
                            fieldToRender = (
                                <MultipleFuzzySingleV2
                                    key={field.id}
                                    {...multipleFuzzySearchSingleProps}
                                />
                            );
                        } else {
                            fieldToRender = (
                                <MultipleFuzzySingle
                                    key={field.id}
                                    {...multipleFuzzySearchSingleProps}
                                />
                            );
                        }
                        break;
                    case 'multipleField':
                        const multipleFieldProps = {
                            fieldId: field.id,
                            label: field.label,
                            inputs: field.inputs,
                            displayBlock: field.displayBlock,
                            labelMode: field.labelMode || 'horizontal',
                            data: data,
                            onChange: changeField,
                            getError: getError,
                            errors: errors,
                            firstErrorField: newFirstErrorField,
                            isBulkAction: isBulkAction,
                            duplicates: duplicates,
                            dynamicHiddenMap: dynamicHiddenMap,
                            onClear: innerClearField,
                            configMap,
                            isNewEntity,
                            originalData,
                            smartFieldOverridesCache,
                            environmentField:
                                field.id !== environmentField?.id ? environmentField : undefined,
                        };

                        if (version === 2) {
                            fieldToRender = (
                                <MultipleCrudFieldV2 key={field.id} {...multipleFieldProps} />
                            );
                        } else {
                            fieldToRender = (
                                <MultipleCrudField key={field.id} {...multipleFieldProps} />
                            );
                        }

                        break;
                    case 'geolocation':
                        fieldToRender = (
                            <GeoLocationField
                                key={field.id}
                                label={field.label}
                                mandatory={field.mandatory}
                                readOnly={field.readOnly}
                                hidden={field.hidden}
                                labelMode={'horizontal'}
                                data={data}
                                onChange={changeField}
                                inputs={field.inputs}
                                isNewEntity={isNewEntity}
                                configMap={configMap}
                                isBulkAction={isBulkAction}
                                smartFieldOverridesCache={smartFieldOverridesCache}
                                {...field.inputAttrs}
                            />
                        );
                        break;
                    case 'dateTimeGroup': {
                        const dateTimeGroupProps = {
                            label: field.label,
                            field,
                            mandatory: field.mandatory,
                            readOnly: field.readOnly,
                            hidden: field.hidden,
                            data: data,
                            onChange: changeField,
                            fieldId: field.id,
                            errors,
                            description: field.description,
                            ...field.inputAttrs,
                        };

                        if (version === 2) {
                            fieldToRender = (
                                <DateTimeGroupV2 key={field.id} {...dateTimeGroupProps} />
                            );
                        } else {
                            fieldToRender = (
                                <DateTimeGroup key={field.id} {...dateTimeGroupProps} />
                            );
                        }
                    }
                }
                return fieldToRender;
            },
            [
                firstErrorField,
                userInviteFieldActions,
                data,
                changeField,
                errors,
                isBulkAction,
                getFieldsActions,
                innerClearField,
                environmentField,
                version,
                duplicates,
                dynamicHiddenMap,
                configMap,
                isNewEntity,
                originalData,
                smartFieldOverridesCache,
            ],
        );

        const renderTabFields = useCallback(
            (fields, columns) => {
                let classes = classnames('fm-crud-form-tab-fields', {
                    [`fm-crud-form-tab-fields__${columns}-columns`]: columns && columns > 0,
                });

                const finalFields =
                    fields?.filter((field) => {
                        // Use the global smart field overrides cache
                        const smartFieldOverrides = smartFieldOverridesCache.get(field.id) || {};
                        // If smart field has a hidden override, use that value directly
                        if (smartFieldOverrides.hasOwnProperty('hidden')) {
                            return !smartFieldOverrides.hidden;
                        }

                        // Otherwise, use the original filtering logic
                        return !(
                            field.hidden ||
                            (dynamicHiddenMap && dynamicHiddenMap[field.fieldConfiguration])
                        );
                    }) || [];

                const getFieldComponent = (field) => {
                    let inputAttrs = field.inputAttrs;

                    let newFirstErrorField = firstErrorField;
                    if (newFirstErrorField) newFirstErrorField = newFirstErrorField.toLowerCase();

                    // Get parentValue dependency
                    let parentValue = null;

                    if (inputAttrs && inputAttrs.parentField) {
                        const id = configMap[inputAttrs.parentField];
                        parentValue = data[id];
                    }

                    let fieldWorkFlow = getFieldWorkFlow(
                        field.fieldConfiguration,
                        workFlow,
                        idUserType,
                    );
                    const autoFocus = firstFieldToFocus === field.id;

                    const mandatory = isBulkAction ? false : field.mandatory;

                    if (inputAttrs && inputAttrs.beforeRenderCrudField) {
                        field = inputAttrs.beforeRenderCrudField(field);
                    }

                    const dependenceFilter =
                        field && field.dependenceFilter && field.dependenceFilter(field);

                    if (
                        !field.hasOwnProperty('inputs') &&
                        !field.isCustom &&
                        !field.isMultipleField
                    ) {
                        if (parentValue && (parentValue.value || parentValue.Id)) {
                            parentValue = parentValue.value || parentValue.Id;
                        }

                        let menuProps = field.hasInviteUsers
                            ? { actions: userInviteFieldActions }
                            : undefined;

                        let actions = [];

                        if (inputAttrs?.list) {
                            const newActions = getFieldsActions({
                                list: inputAttrs.list,
                                fieldId: field.id,
                                type: field.type,
                                parentFieldForContext: inputAttrs.parentFieldForContext || '',
                                parentFieldForContext2: inputAttrs.parentFieldForContext2 || '',
                            });
                            if (!menuProps) menuProps = {};
                            menuProps.actions = [
                                ...(menuProps?.actions || []),
                                ...(newActions || []),
                            ];

                            // Actions are for crudV2... and for value list actions from crudV1
                            if (version === 2) {
                                actions = menuProps.actions;
                            } else {
                                actions = inputAttrs?.actions || [];
                            }
                        }

                        const willBeCleared = getWillBeCleared(field);

                        // Check for warning description based on warningField
                        let finalDescription = field.description;
                        let finalClassName = field.className || '';

                        if (field.warningField) {
                            const state = Context.store.getState();
                            const permissions = state.config?.permission || {};

                            // Support both string and object formats for warningField
                            const dataField =
                                typeof field.warningField === 'string'
                                    ? field.warningField
                                    : field.warningField.dataField;
                            const requiredPermission =
                                typeof field.warningField === 'string'
                                    ? null
                                    : field.warningField.permission;

                            const hasPermission =
                                permissions.warnings &&
                                (!requiredPermission || permissions[requiredPermission]);

                            if (hasPermission && data[dataField]) {
                                finalDescription = data[dataField];
                                finalClassName = finalClassName
                                    ? `${finalClassName} fm-field-description-warning`
                                    : 'fm-field-description-warning';
                            }
                        }

                        const baseProps = {
                            fieldId: field.id,
                            type: field.type,
                            label: field.label,
                            labelMode: field.labelMode || 'horizontal',
                            hint: field.hint,
                            description: finalDescription,
                            mandatory: mandatory,
                            maxLength: field.dataLength,
                            limitInput: field.limitInput,
                            readOnly:
                                field.isMultiEnvironmentField &&
                                (!data[field.id] ||
                                    data[field.id] === '0' ||
                                    data[field.id] === '-1') &&
                                !isNewEntity
                                    ? false
                                    : field.readOnly,
                            hidden: field.hidden,
                            inputAttrs: inputAttrs,
                            value: data[field.id],
                            parentValue: parentValue,
                            dependenceFilter: dependenceFilter,
                            onChange: innerChangeField(field),
                            onClear: innerClearField(field),
                            error: getError(errors[field.id], [field.dataLength]),
                            firstErrorField:
                                newFirstErrorField && field.id.toLowerCase() === newFirstErrorField,
                            className: finalClassName,
                            isBulkAction: isBulkAction,
                            spaced: field.spaced,
                            workFlow: fieldWorkFlow,
                            isNewEntity: isNewEntity,
                            duplicates: duplicates,
                            includeTime: field.includeTime,
                            component: field.component,
                            version: version,
                            autoFocus: autoFocus,
                            menuProps: menuProps,
                            actions: actions,
                            canClear: !field.readOnly && !field.mandatory,
                            willBeCleared,
                            preAdvice: field.preAdvice,
                        };

                        // Use the memoized smart field overrides cache
                        const smartFieldOverrides = smartFieldOverridesCache.get(field.id) || {};

                        const { defaultValue, ...otherOverrides } = smartFieldOverrides;
                        const hasDefaultValue =
                            !!Object.entries(otherOverrides).length && !!defaultValue;
                        const parentFieldId = configMap[field.smartField?.parentFieldId];
                        const showSmartFieldDefault =
                            isTouched({ data, originalData, field: parentFieldId }) &&
                            hasDefaultValue;

                        let finalValue = showSmartFieldDefault ? defaultValue : data[field.id];

                        if (showSmartFieldDefault && field.type === 'multipleValueList') {
                            finalValue = getValuesFromList({
                                list: inputAttrs.list,
                                values: finalValue,
                            });
                        }

                        const crudFieldProps = {
                            ...baseProps,
                            value: finalValue,
                            environmentField:
                                field.id !== environmentField?.id ? environmentField : undefined,
                            ...otherOverrides,
                        };

                        return <CrudField key={field.id} {...crudFieldProps} />;
                    } else if (field.isCustom) {
                        const getCustomField = getRenderCustomField(entity) || null;
                        if (!getCustomField) return null;
                        return getCustomField({
                            field,
                            data,
                            changeField,
                            errors,
                            getError,
                            firstErrorField: newFirstErrorField,
                            isBulkAction,
                            version,
                            configMap,
                            isNewEntity,
                        });
                    } else {
                        return renderMultipleFields(field, parentValue);
                    }
                };

                if (columns) {
                    return (
                        <div className={classes}>
                            {Array.from(Array(columns)).map((_, column) => (
                                <div key={column} className="fm-crud-form-tab-fields__column">
                                    {finalFields
                                        .filter((field) => field.column === column + 1)
                                        .map(getFieldComponent)}
                                </div>
                            ))}
                        </div>
                    );
                } else {
                    return <div className={classes}>{finalFields.map(getFieldComponent)}</div>;
                }
            },
            [
                dynamicHiddenMap,
                firstErrorField,
                workFlow,
                idUserType,
                firstFieldToFocus,
                isBulkAction,
                configMap,
                data,
                userInviteFieldActions,
                innerChangeField,
                innerClearField,
                errors,
                isNewEntity,
                duplicates,
                version,
                originalData,
                environmentField,
                getFieldsActions,
                entity,
                changeField,
                renderMultipleFields,
                smartFieldOverridesCache,
            ],
        );

        const chevronClick = useCallback(() => {
            setCollapsed(!collapsed);
        }, [collapsed]);

        let collapsedClass = collapsed ? 'chevron-collapsed' : '';

        if (!tab?.show || !tab?.fields || tab?.fields.length === 0) return null;
        let fieldsToPrint = tab.fields.filter((field) => {
            // Helper function to check if a field should be shown
            const shouldShowField = (fieldToCheck) => {
                const smartFieldOverrides = smartFieldOverridesCache.get(fieldToCheck.id) || {};

                // In bulk actions, show fields with smartfield definitions
                if (isBulkAction && smartFieldOverrides.hasSmartField) {
                    return true;
                }

                // If smart field has a hidden override, use that value directly
                if (smartFieldOverrides.hasOwnProperty('hidden')) {
                    return !smartFieldOverrides.hidden;
                }

                // Otherwise, use the original hidden logic
                if (fieldToCheck.hidden) return false;
                if (dynamicHiddenMap && dynamicHiddenMap[fieldToCheck.fieldConfiguration])
                    return false;

                return true;
            };

            // Check the main field
            if (!shouldShowField(field)) return false;

            // For fields with inputs (multipleField, etc.), check if at least one input is visible
            if (field.inputs && Array.isArray(field.inputs)) {
                const hasVisibleInput = field.inputs.some((inputField) =>
                    shouldShowField(inputField),
                );
                // If no inputs are visible, hide the parent field
                if (!hasVisibleInput) return false;
            }

            if (environmentField && field.isMultiEnvironmentField) return true;
            if (environmentField && !environmentField?.value && field.id !== environmentField?.id)
                return false;
            return true;
        });
        if (fieldsToPrint.length === 0) return null;

        if (!tab.title) {
            return (
                <div className="fm-crud-form-tab" ref={innerRef}>
                    {renderTabFields(tab.fields, tab.columns)}
                    <WarningChangeModal onRef={onRefModal} />
                </div>
            );
        } else {
            const showEnvironmentFieldAdvice =
                environmentField && indexTab === 0 && isNewEntity && !environmentField?.value;
            return (
                <div className="fm-crud-form-tab" ref={innerRef}>
                    <div className="fm-crud-form-tab-title">
                        <div className="tab-title-text">{tab.title}</div>
                        {!postComponentHeader && (
                            <div
                                className={`tab-title-chevron ${collapsedClass}`}
                                onClick={chevronClick}
                            >
                                {version !== 2 && <ChevronUp />}
                                {version === 2 && (
                                    <Icon
                                        name="expandLess"
                                        size="large"
                                        color={colors.utility.textSecondary}
                                    />
                                )}
                            </div>
                        )}
                        {postComponentHeader}
                    </div>
                    {showEnvironmentFieldAdvice && (
                        <Advice type="semanticInfo" showIcon className="fm-crud-form-tab__advice">
                            {getLiteral('label_multi_environment_select_environment_crud_advice')}
                        </Advice>
                    )}
                    <AnimatedHeight open={!collapsed}>
                        {renderTabFields(fieldsToPrint, tab.columns)}
                    </AnimatedHeight>
                    <WarningChangeModal onRef={onRefModal} />
                    <WarningDependencyModal
                        onClose={() => setDependencyModalContent(null)}
                        schema={schema}
                        {...dependencyModalContent}
                    />
                </div>
            );
        }
    },
);

export default connect(mapStateToProps, mapDispatchToProps)(EntityCrudTab);
